import { FunctionComponent, useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDisclosure } from "@chakra-ui/react";
import { IconSelector } from "@tabler/icons-react";

import { Page } from "../Page";
import { ReportPanel } from "./ReportPanel/ReportPanel";
import { EntityPanel } from "./EntityPanel/EntityPanel";
import { TransactionPanel } from "./TransactionPanel/TransactionPanel";
import { DashboardPanel } from "./DashboardPanel/DashboardPanel";
import { BenchmarkPanel } from "./BenchmarkingPanel/BenchmarkPanel";

import { useGetClient } from "../../api/clients/clientQueries";
import { useGetClients } from "../../api/clients/clientQueries";
import { useCurrentUser } from "../../api/users/userQueries";
import { ClientSelectorModal } from "./ClientSelectorModal";

export const ClientAdminPage: FunctionComponent = () => {
    const params = useParams();
    const { clientId, tabId, itemId } = params;
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const { data: client } = useGetClient(clientId);
    const { data: clients = [] } = useGetClients();
    const { data: currentUser } = useCurrentUser();
    const clientName = client ? client.name : "Loading...";

    const filteredClients = useMemo(() => {
        return clients.filter(
            client =>
                client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (client.agressoCode && client.agressoCode.toLowerCase().includes(searchTerm.toLowerCase())),
        );
    }, [searchTerm, clients]);

    const tabConfig = useMemo(
        () => [
            { id: "dashboard", name: "Dashboard", component: <DashboardPanel clientId={clientId} /> },
            { id: "entities", name: "Entities", component: <EntityPanel clientId={clientId} /> },
            { id: "transactions", name: "Transactions", component: <TransactionPanel clientId={clientId} /> },
            { id: "reports", name: "Reports", component: <ReportPanel clientId={clientId} /> },
            { id: "benchmarking", name: "Benchmarking", component: <BenchmarkPanel clientId={clientId} /> },
        ],
        [clientId, itemId],
    );

    useEffect(() => {
        const tabIndex = tabConfig.findIndex(tab => tab.id === tabId);
        setSelectedTabIndex(tabIndex === -1 ? 0 : tabIndex);
    }, [tabId, tabConfig]);

    const CurrentPanel = tabConfig[selectedTabIndex]?.component || null;

    // Dynamic breadcrumb navigation based on user role
    const breadcrumbOptions = currentUser?.isSystemAdmin
        ? [{ name: "System Admin", label: "System Admin", onClick: () => navigate("/admin") }]
        : [{ name: "Clients", label: "Clients", onClick: () => navigate("/client-portals") }];

    const handleClientSelect = (selectedClientId: string) => {
        onClose();
        setSearchTerm("");
        navigate(`/clients/${selectedClientId}/dashboard`);
    };

    // Build actions array for top navigation
    const allowedActions = [];

    // Add client selector if there are multiple clients
    if (clients.length > 1) {
        allowedActions.push({
            name: clientName,
            onClick: onOpen,
            icon: <IconSelector />,
            label: "Switch to a different client portal",
        });
    }

    return (
        <>
            <Page
                title={clientName}
                topNavProps={{
                    showLogo: true,
                    breadcrumbs: breadcrumbOptions,
                    actions: allowedActions,
                    tabs: {
                        selectedIndex: selectedTabIndex,
                        tabs: tabConfig.map(tab => ({
                            name: tab.name,
                            onClick: () => navigate(`/clients/${clientId}/${tab.id}`),
                        })),
                    },
                }}
            >
                {CurrentPanel}
            </Page>

            <ClientSelectorModal
                isOpen={isOpen}
                onClose={onClose}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                filteredClients={filteredClients}
                clientId={clientId || ""}
                handleClientSelect={handleClientSelect}
            />
        </>
    );
};
